import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useCallback
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectAvailableOrganizations, selectUser } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { Permissions } from 'lib/permissions/roles';
import { getRoleHasPermissionForAllAllowedOrganizationsForPublisherUser } from 'lib/frontend/utils/users';
import { EOrganization, ESnapshotExists, exists } from 'lib/types';
import { OrganizationStatus } from 'lib/enums';

type ReportParamsGetDateRange = {
  dateRange: string;
  startDate?: string;
  endDate?: string;
};

export type GetDateRangeFn = () => ReportParamsGetDateRange;

type ReportParamsContextType = {
  deleteParams: (keys: string[]) => void;
  updateOrgIds: (orgIds: string[]) => void;
  getOrgIds: () => string[];
  getOrgIdsWithPermission: (permission: Permissions) => string[];
  getAvailableOrgIdsWithPermission: (permission: Permissions) => string[];
  getDateRange: GetDateRangeFn;
  updateDateRange: (value: string) => void;
  handleDateRangeChange: (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => void;
};

const ReportParamsContext = createContext<ReportParamsContextType | undefined>(
  undefined
);

export const orgHasRelevantStatusForReport = (
  org: ESnapshotExists<EOrganization>
) => {
  return [
    OrganizationStatus.live.value,
    OrganizationStatus.in_implementation.value,
    OrganizationStatus.placement_only.value,
    OrganizationStatus.not_applicable.value // this is for organizations that accept classifieds and obituaries but do not accept notices
  ].includes(org.data().organizationStatus);
};

export function ReportParamsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const availableOrganizations = useAppSelector(selectAvailableOrganizations);
  const user = useAppSelector(selectUser);

  // Set default parameters if none are present
  useEffect(() => {
    const hasDateRange = searchParams.has('dateRange');
    const hasOrgIds = searchParams.has('orgIds');

    if (!hasDateRange || !hasOrgIds) {
      const newParams = new URLSearchParams(searchParams);

      if (!hasDateRange) {
        newParams.set('dateRange', 'last30days');
      }

      if (!hasOrgIds) {
        newParams.set(
          'orgIds',
          availableOrganizations
            .filter(org => orgHasRelevantStatusForReport(org))
            .map(org => org.id)
            .join(',')
        );
      }

      setSearchParams(newParams, { replace: true });
    }
  }, []);

  const updateParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (!value) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  const deleteParams = (keys: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    keys.forEach(key => {
      newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const updateOrgIds = (orgIds: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('orgIds', orgIds.join(','));
    setSearchParams(newParams, { replace: true });
  };

  const getOrgIds = useCallback(() => {
    const orgIdsParam = searchParams.get('orgIds') || '';
    return orgIdsParam ? orgIdsParam.split(',').filter(Boolean) : [];
  }, [searchParams]);

  const getAvailableOrgIdsWithPermission = useCallback(
    (permission: Permissions) => {
      if (!exists(user)) {
        return [];
      }

      const [permissionError, permissionSet] =
        getRoleHasPermissionForAllAllowedOrganizationsForPublisherUser(
          user,
          permission
        );

      if (permissionError || !permissionSet) {
        return [];
      }

      const availableOrgIds = availableOrganizations?.map(org => org.id) ?? [];
      return availableOrgIds.filter(orgId => permissionSet.has(orgId));
    },
    [user, getOrgIds]
  );

  const getOrgIdsWithPermission = useCallback(
    (permission: Permissions) => {
      const availableOrgIdsSet = new Set(
        getAvailableOrgIdsWithPermission(permission)
      );
      return getOrgIds().filter(orgId => availableOrgIdsSet.has(orgId));
    },
    [getOrgIds, getAvailableOrgIdsWithPermission]
  );

  const getDateRange = () => {
    const dateRange = searchParams.get('dateRange') || '';
    if (dateRange === 'custom') {
      const startDate = searchParams.get('startDate') || '';
      const endDate = searchParams.get('endDate') || '';
      return { dateRange, startDate, endDate };
    }
    return { dateRange };
  };

  const updateDateRange = (dateRange: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('dateRange', dateRange);

    // Clear custom date range params when switching to a preset
    if (dateRange !== 'custom') {
      newParams.delete('startDate');
      newParams.delete('endDate');
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleDateRangeChange = useMemo(
    () => (startDate: Date | undefined, endDate: Date | undefined) => {
      if (startDate && endDate) {
        const params: Record<string, string> = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          dateRange: 'custom'
        };
        updateParams(params);
      }
    },
    [updateDateRange, updateParams]
  );

  const value = useMemo(
    () => ({
      deleteParams,
      getDateRange,
      updateDateRange,
      handleDateRangeChange,
      getOrgIds,
      getOrgIdsWithPermission,
      getAvailableOrgIdsWithPermission,
      updateOrgIds
    }),
    [
      deleteParams,
      getDateRange,
      updateDateRange,
      handleDateRangeChange,
      getOrgIds,
      getOrgIdsWithPermission,
      getAvailableOrgIdsWithPermission,
      updateOrgIds
    ]
  );

  return (
    <ReportParamsContext.Provider value={value}>
      {children}
    </ReportParamsContext.Provider>
  );
}

export function useReportParams() {
  const context = useContext(ReportParamsContext);
  if (!context) {
    throw new Error(
      'useReportParams must be used within a ReportParamsProvider'
    );
  }
  return context;
}
